/**
 * The calendar page: Mahmoud's layout of 2026-10-10, in his order.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 *   1. The calendar first, full width, the YEAR sheet by default; quarter and month are
 *      the same data closer up. Planned activities are round marks, RFIs square ones,
 *      and one legend says what every mark and shade means.
 *   2. A picture of what the registers below hold, month by month.
 *   3. The registers themselves: the scheduled inspections, the RFIs, and the work still
 *      owed a date.
 * And the calendar's own act, "Plan an activity": the contract, the activity, the
 * discipline, the dates, the contractor; it lands in the project's draft schedule and
 * goes for approval.
 *
 * Every row is what the API answered for THIS principal: the operator sees the
 * assessors the server names, the contractor sees its own dates. A failed read renders
 * the refusal panel, an empty view the legend and an empty register, never a blank box.
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";

import {
  getPeriodCreateOptions,
  listCalendarActivities,
  listCalendarPeriods,
  type CalendarActivity,
} from "../api/calendar";
import { ApiError } from "../api/client";
import { listCandidates, type Candidate } from "../api/itps";
import { getTaxonomies, listContracts } from "../api/portfolio";
import { listRfis, type RfiSummary } from "../api/rfis";
import {
  listPlanningProjects,
  listSchedules,
  planFromCalendar,
  type PlanningProject,
} from "../api/schedules";
import { useToken } from "../auth/sessionContext";
import { strings } from "../brand/strings";
import { StackedBarChart } from "../charts/StackedBarChart";
import { ServerField } from "../lib/serverErrors";
import { ActionDialog, ToastBar } from "../nav/ActionDialog";
import { Checkbox } from "@/components/ui/checkbox";
import { DataTable, type RegisterColumn } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { QDrawer } from "../nav/primitives";
import {
  AbsencePanel,
  EmptyState,
  LoadingLine,
  Section,
  StateChip,
  day as renderDay,
} from "../screens/parts";
import { DateCheck } from "../screens/planning";
import { useQuery } from "../screens/useQuery";
import { YearGrid } from "../screens/YearGrid";

import { monthCells, toISO } from "./dateMath";
import { PICKER_CLASS, PickerOption, pickerWidth } from "../nav/PickerOption";

import { BlockedPeriodDialog } from "./BlockedPeriodDialog";
import {
  AttentionLegend,
  activityAttention,
  activityTone,
  enforcementShade,
  rfiAttention,
  rfiTone,
  type CalendarCell,
  type CalendarDot,
} from "./model";
import { MonthGrid } from "./MonthGrid";
import { QuarterGrid, type QuarterMonth } from "./QuarterGrid";

interface Filters {
  readonly discipline: string | null;
  readonly projectId: string | null;
}

/** Wave Experience A: the hub calendar's category filter (what kind of entry is drawn). */
type Category = "all" | "activity" | "rfi";

type ViewMode = "month" | "quarter" | "year";

function nowParts(): { year: number; month: number } {
  const today = new Date();
  return { year: today.getFullYear(), month: today.getMonth() };
}

function shadeForDate(
  iso: string,
  periods: readonly { startsOn: string; endsOn: string; enforcement: string }[],
): "block" | "warn" | null {
  let shade: "block" | "warn" | null = null;
  for (const period of periods) {
    if (iso >= period.startsOn && iso <= period.endsOn) {
      const one = enforcementShade(period.enforcement);
      if (one === "block") {
        return "block";
      }
      if (one === "warn") {
        shade = "warn";
      }
    }
  }
  return shade;
}

/** The anchor of the unplanned work section: the planning candidates view (R06). */
export const UNPLANNED_ANCHOR = "unplanned-work";

/** A `?day=YYYY-MM-DD` link (the dashboard's month card) opens that day on its month. */
function dayFromSearch(search: string): string | null {
  const day = new URLSearchParams(search).get("day");
  return day !== null && /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : null;
}

const MONTH_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** The YearGrid reads the schedule's activity shape; the calendar row maps onto it. */
function asScheduleActivity(
  activity: CalendarActivity,
): Parameters<typeof YearGrid>[0]["activities"][number] {
  return {
    id: activity.id,
    humanId: activity.humanId,
    scheduleId: activity.scheduleId,
    scheduleWorkflowState: "",
    plannedDate: activity.plannedDate,
    plannedEndDate: activity.plannedEndDate,
    status: activity.status,
    isTerminal: false,
    priority: activity.priority,
    purpose: activity.purpose,
    checklistReference: null,
    contractorPartyId: activity.contractorPartyId,
    contractorPartyName: activity.contractorPartyName,
    contractId: null,
    verificationTypeCode: activity.verificationTypeCode,
    verificationTypeLabel: activity.verificationTypeLabel,
    assessorPersonIds: [],
    assessorDisplayNames: [],
    negotiationState: activity.negotiationState,
    pendingDate: activity.pendingDate,
    availableMoves: [],
    availableEvents: [],
    inspectionId: null,
    mayConductInspection: false,
    actionableByMe: activity.actionableByMe === true,
  };
}

export function CalendarPage(): React.JSX.Element {
  const token = useToken();
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const linkedDay = dayFromSearch(location.search);
  const projectParam = searchParams.get("project") ?? searchParams.get("projectId");
  const initial =
    linkedDay === null
      ? nowParts()
      : { year: Number(linkedDay.slice(0, 4)), month: Number(linkedDay.slice(5, 7)) - 1 };
  // His order: the YEAR calendar first. A link that names a day opens that day's month.
  const [view, setView] = useState<ViewMode>(linkedDay === null ? "year" : "month");
  const [cursor, setCursor] = useState(initial);
  const [filters, setFilters] = useState<Filters>({
    discipline: null,
    projectId: projectParam,
  });
  const [openDay, setOpenDay] = useState<string | null>(linkedDay);
  const [reload, setReload] = useState(0);
  // Wave Experience A: the quick action's ?act=plan opens the dialog every time it is
  // followed (read from the URL, not once at mount); closing drops it from the URL.
  const [planByHand, setPlanOpen] = useState(false);
  const actPlan = searchParams.get("act") === "plan";
  const planOpen = planByHand || actPlan;
  const navigateTo = useNavigate();
  const closePlan = (): void => {
    setPlanOpen(false);
    if (actPlan) {
      const next = new URLSearchParams(location.search);
      next.delete("act");
      const rest = next.toString();
      void navigateTo(
        { pathname: location.pathname, search: rest === "" ? "" : `?${rest}`, hash: location.hash },
        { replace: true },
      );
    }
  };
  const [toast, setToast] = useState<{ message: string; scheduleId: string } | null>(null);
  const [category, setCategory] = useState<Category>("all");
  const [blockedOpen, setBlockedOpen] = useState(false);
  const [periodNote, setPeriodNote] = useState<string | null>(null);
  // The server's word for "Add blocked period": where the caller may declare one.
  const periodOptions = useQuery((signal) => getPeriodCreateOptions(token, signal), [token]);
  const periodKinds = useQuery((signal) => getTaxonomies(token, signal), [token]);
  const mayBlock =
    periodOptions.kind === "ready" &&
    (periodOptions.data.tenantWide || periodOptions.data.projects.length > 0);

  // When filtered to a project, fetch its contracts and schedules to scope the feeds.
  const filteredContracts = useQuery(
    (signal) =>
      filters.projectId
        ? listContracts(token, { projectId: filters.projectId, signal })
        : Promise.resolve({ total: 0, items: [] }),
    [token, filters.projectId],
  );
  const filteredSchedules = useQuery(
    (signal) =>
      filters.projectId
        ? listSchedules(token, { projectId: filters.projectId, signal })
        : Promise.resolve({ total: 0, items: [] }),
    [token, filters.projectId],
  );
  const projectContractIds = useMemo(() => {
    if (!filters.projectId || filteredContracts.kind !== "ready") {
      return null;
    }
    return new Set(filteredContracts.data.items.map((c) => c.id));
  }, [filters.projectId, filteredContracts]);
  const projectScheduleIds = useMemo(() => {
    if (!filters.projectId || filteredSchedules.kind !== "ready") {
      return null;
    }
    return new Set(filteredSchedules.data.items.map((s) => s.id));
  }, [filters.projectId, filteredSchedules]);

  // One read for the whole year in view: the year sheet, the quarter and the month are
  // closer looks at it, and the picture below counts it month by month.
  const yearFrom = toISO(cursor.year, 0, 1);
  const yearTo = toISO(cursor.year, 11, 31);
  const activities = useQuery(
    (signal) =>
      listCalendarActivities(token, { fromDate: yearFrom, toDate: yearTo, limit: 500, signal }),
    [token, yearFrom, yearTo, reload],
  );
  const rfis = useQuery(
    (signal) => listRfis(token, { fromDate: yearFrom, toDate: yearTo, signal }),
    [token, yearFrom, yearTo, reload],
  );
  const periods = useQuery((signal) => listCalendarPeriods(token, signal), [token, reload]);
  const candidates = useQuery(
    (signal) => listCandidates(token, { unplannedOnly: true, signal }),
    [token, reload],
  );
  const planningProjects = useQuery((signal) => listPlanningProjects(token, signal), [token]);

  // The planning candidates entry (wave W1, R06) lands on this page's unplanned work:
  // once that register has answered, bring it into view.
  const candidatesSettled = candidates.kind !== "loading";
  useEffect(() => {
    if (location.hash !== `#${UNPLANNED_ANCHOR}` || !candidatesSettled) {
      return;
    }
    const target = document.getElementById(UNPLANNED_ANCHOR);
    if (target !== null && typeof target.scrollIntoView === "function") {
      target.scrollIntoView({ block: "start" });
    }
  }, [location.hash, candidatesSettled]);

  const yearActivities = useMemo(
    () =>
      (activities.kind === "ready" ? activities.data.items : []).filter((activity) => {
        if (category === "rfi") {
          return false;
        }
        if (filters.discipline !== null && activity.disciplineCode !== filters.discipline) {
          return false;
        }
        if (projectScheduleIds !== null && !projectScheduleIds.has(activity.scheduleId)) {
          return false;
        }
        return true;
      }),
    [activities, filters.discipline, projectScheduleIds, category],
  );
  const yearRfis = useMemo(
    () =>
      (rfis.kind === "ready" ? rfis.data.items : []).filter((rfi) => {
        if (category === "activity") {
          return false;
        }
        if (projectContractIds !== null && !projectContractIds.has(rfi.contractId)) {
          return false;
        }
        return true;
      }),
    [rfis, projectContractIds, category],
  );
  const unplannedItems = useMemo(() => {
    if (candidates.kind !== "ready") {
      return [];
    }
    if (projectContractIds === null) {
      return candidates.data.items;
    }
    return candidates.data.items.filter((c) => projectContractIds.has(c.contractId));
  }, [candidates, projectContractIds]);
  const periodList = useMemo(() => (periods.kind === "ready" ? periods.data : []), [periods]);

  /** Every mark on every day of the year: round activities, square RFIs. */
  const marksByDate = useMemo(() => {
    const map = new Map<string, CalendarDot[]>();
    const add = (iso: string, dot: CalendarDot): void => {
      const list = map.get(iso) ?? [];
      list.push(dot);
      map.set(iso, list);
    };
    for (const one of yearActivities) {
      add(one.plannedDate, {
        tone: activityAttention(one),
        kind: "activity",
        label: `${one.humanId} · ${strings.schedules.negotiation[one.negotiationState] ?? one.statusLabel}`,
      });
    }
    for (const one of yearRfis) {
      add(one.plannedDate, {
        tone: rfiAttention(one),
        kind: "rfi",
        label: `${one.humanId} · ${strings.rfis.dateState[one.dateState] ?? one.dateState}`,
      });
    }
    return map;
  }, [yearActivities, yearRfis]);

  const cellsFor = (
    year: number,
    month: number,
  ): (CalendarCell & { year: number; month: number })[] =>
    monthCells(year, month).map((cell) => ({
      ...cell,
      dots: marksByDate.get(cell.iso) ?? [],
      shade: shadeForDate(cell.iso, periodList),
    }));

  const quarter = (Math.floor(cursor.month / 3) + 1) as 1 | 2 | 3 | 4;
  const quarterMonths: QuarterMonth[] = [0, 1, 2].map((offset) => {
    const month = (quarter - 1) * 3 + offset;
    return { year: cursor.year, month, cells: cellsFor(cursor.year, month) };
  });

  /** The picture of the registers: activities and RFIs per month by where the date stands. */
  const chartData = MONTH_SHORT.map((label, index) => {
    const prefix = `${String(cursor.year)}-${String(index + 1).padStart(2, "0")}`;
    const inMonth = yearActivities.filter((one) => one.plannedDate.startsWith(prefix));
    const rfiInMonth = yearRfis.filter((one) => one.plannedDate.startsWith(prefix));
    const count = (list: readonly { tone: string }[], tone: string): number =>
      list.filter((one) => one.tone === tone).length;
    const activityTones = inMonth.map((one) => ({ tone: activityTone(one) }));
    const rfiTones = rfiInMonth.map((one) => ({ tone: rfiTone(one) }));
    return {
      label,
      segments: [
        { key: "agreed", value: count(activityTones, "agreed") + count(activityTones, "done") },
        { key: "waiting", value: count(activityTones, "waiting") },
        { key: "draft", value: count(activityTones, "draft") },
        { key: "rfiAgreed", value: count(rfiTones, "agreed") + count(rfiTones, "done") },
        { key: "rfiWaiting", value: count(rfiTones, "waiting") },
      ],
    };
  });

  const activityColumns = useMemo<RegisterColumn<CalendarActivity>[]>(
    () => [
      {
        id: "activity",
        label: strings.schedules.columnActivity,
        text: (row) => (row.purpose !== null ? `${row.humanId} ${row.purpose}` : row.humanId),
        sortValue: (row) => row.humanId,
        render: (row) => (
          <div>
            <Link to={`/activities/${row.id}`} className="font-mono text-link hover:underline">
              {row.humanId}
            </Link>
            {row.purpose !== null && (
              <div className="text-xs text-muted-foreground">{row.purpose}</div>
            )}
          </div>
        ),
      },
      {
        id: "date",
        label: strings.schedules.columnDate,
        text: (row) => renderDay(row.plannedDate),
        sortValue: (row) => row.plannedDate,
      },
      {
        id: "dateState",
        label: strings.schedules.columnDateState,
        text: (row) => strings.schedules.negotiation[row.negotiationState] ?? row.negotiationState,
      },
      {
        id: "type",
        label: strings.schedules.columnType,
        text: (row) => row.verificationTypeLabel,
      },
      {
        id: "contractor",
        label: strings.schedules.columnContractor,
        text: (row) => row.contractorPartyName,
      },
      {
        id: "status",
        label: strings.schedules.columnStatus,
        text: (row) => row.statusLabel,
        filter: true,
        render: (row) => <StateChip state={row.status} label={row.statusLabel} />,
      },
    ],
    [],
  );

  const rfiColumns = useMemo<RegisterColumn<RfiSummary>[]>(
    () => [
      {
        id: "rfi",
        label: strings.rfis.columnRfi,
        text: (row) => row.humanId,
        sortValue: (row) => row.humanId,
        render: (row) => (
          <Link to={`/rfis/${row.id}`} className="font-mono text-link hover:underline">
            {row.humanId}
          </Link>
        ),
      },
      {
        id: "date",
        label: strings.rfis.columnDate,
        text: (row) => renderDay(row.plannedDate),
        sortValue: (row) => row.plannedDate,
      },
      {
        id: "state",
        label: strings.rfis.columnState,
        text: (row) => strings.rfis.dateState[row.dateState] ?? row.dateState,
      },
      {
        id: "scope",
        label: strings.rfis.columnScope,
        text: (row) => row.scope,
      },
      {
        id: "contractor",
        label: strings.schedules.columnContractor,
        text: (row) => row.contractorPartyName,
      },
    ],
    [],
  );

  const unplannedColumns = useMemo<RegisterColumn<Candidate>[]>(
    () => [
      {
        id: "plan",
        label: strings.itps.columnPlan,
        text: (entry) => `${entry.itpHumanId} ${entry.itemNo}`,
        sortValue: (entry) => entry.itpHumanId,
        render: (entry) => (
          <div>
            <Link to={`/itps/${entry.itpId}`} className="font-mono text-link hover:underline">
              {entry.itpHumanId}
            </Link>{" "}
            <span className="text-xs text-muted-foreground">{entry.itemNo}</span>
          </div>
        ),
      },
      {
        id: "activity",
        label: strings.schedules.columnActivity,
        text: (entry) => entry.activityDescription,
      },
      {
        id: "type",
        label: strings.schedules.columnType,
        text: (entry) => entry.interventionLabel,
      },
      {
        id: "contractor",
        label: strings.schedules.columnContractor,
        text: (entry) => entry.contractorPartyName,
      },
    ],
    [],
  );

  const disciplineOptions = [
    ...new Set(
      (activities.kind === "ready" ? activities.data.items : [])
        .map((one) => one.disciplineCode)
        .filter((one) => one !== null),
    ),
  ].map((code) => {
    const label =
      (activities.kind === "ready" ? activities.data.items : []).find(
        (one) => one.disciplineCode === code,
      )?.disciplineLabel ?? code;
    return { token: code, label };
  });

  function moveSteps(steps: number): void {
    setCursor((current) => {
      const stride = view === "year" ? 12 : view === "quarter" ? 3 : 1;
      const shifted = new Date(Date.UTC(current.year, current.month + steps * stride, 1));
      return { year: shifted.getUTCFullYear(), month: shifted.getUTCMonth() };
    });
  }

  const periodHeading =
    view === "month"
      ? strings.calendar.monthOf(strings.calendar.monthNames[cursor.month] ?? "", cursor.year)
      : view === "year"
        ? strings.calendar.yearOf(cursor.year)
        : `${strings.calendar.quarterOf(quarter, cursor.year)}: ${strings.calendar.quarterMonths(
            quarterMonths.map((one) => strings.calendar.monthNames[one.month] ?? ""),
          )}`;

  const dayActivities =
    openDay === null ? [] : yearActivities.filter((one) => one.plannedDate === openDay);
  const dayRfis = openDay === null ? [] : yearRfis.filter((one) => one.plannedDate === openDay);
  const mayPlan = planningProjects.kind === "ready" && planningProjects.data.length > 0;

  if (activities.kind === "failed" || periods.kind === "failed") {
    return (
      <div className="q-refusal" role="alert" data-testid="refusal">
        <p className="q-refusal__heading">{strings.calendar.failed}</p>
      </div>
    );
  }

  return (
    <main className="q-page">
      <h2 className="q-page__heading">{strings.calendar.heading}</h2>
      <p className="q-section__lede">{strings.calendar.lede}</p>
      {toast !== null && (
        <ToastBar
          message={toast.message}
          onDone={() => {
            setToast(null);
          }}
        />
      )}
      {toast !== null && (
        <p className="q-note" data-testid="plan-done">
          {toast.message}{" "}
          <Link className="q-link" to={`/schedules/${toast.scheduleId}`}>
            {strings.calendar.openSchedule}
          </Link>
        </p>
      )}
      {periodNote !== null && (
        <ToastBar
          message={periodNote}
          onDone={() => {
            setPeriodNote(null);
          }}
        />
      )}
      {blockedOpen && periodOptions.kind === "ready" && (
        <BlockedPeriodDialog
          options={periodOptions.data}
          kinds={periodKinds.kind === "ready" ? (periodKinds.data.calendarPeriodKinds ?? []) : []}
          onClose={() => {
            setBlockedOpen(false);
          }}
          onAdded={(name) => {
            setBlockedOpen(false);
            setReload((one) => one + 1);
            setPeriodNote(strings.calendar.blockedAdded(name));
          }}
        />
      )}
      {(activities.kind === "loading" || periods.kind === "loading") && (
        <LoadingLine label={strings.calendar.loading} />
      )}

      {/* 1. The calendar, full width. */}
      <div className="q-cal" data-testid="calendar-page">
        <div className="q-cal__main">
          <div className="q-cal__toolbar flex items-center justify-between gap-4">
            <h3 className="q-cal__period min-w-[16rem] shrink-0 text-lg font-semibold">
              {periodHeading}
            </h3>
            <div className="q-actions-row flex items-center gap-2 shrink-0">
              {mayBlock && (
                <button
                  type="button"
                  className="q-button"
                  data-testid="calendar-add-blocked-period"
                  onClick={() => {
                    setBlockedOpen(true);
                  }}
                >
                  {strings.calendar.addBlockedPeriod}
                </button>
              )}
              {mayPlan && (
                <button
                  type="button"
                  className="q-button q-mini--primary"
                  data-testid="calendar-plan-activity"
                  onClick={() => {
                    setPlanOpen(true);
                  }}
                >
                  {strings.calendar.planActivity}
                </button>
              )}
              <div
                className="q-cal__viewswitch flex items-center shrink-0"
                data-testid="calendar-control-group"
              >
                <button
                  type="button"
                  data-testid="calendar-year-view"
                  className={`q-button q-button--mini${view === "year" ? "" : " q-button--quiet"}`}
                  aria-pressed={view === "year"}
                  onClick={() => {
                    setView("year");
                  }}
                >
                  {strings.calendar.yearView}
                </button>
                <button
                  type="button"
                  data-testid="calendar-quarter-view"
                  className={`q-button q-button--mini${view === "quarter" ? "" : " q-button--quiet"}`}
                  aria-pressed={view === "quarter"}
                  onClick={() => {
                    setView("quarter");
                  }}
                >
                  {strings.calendar.quarterView}
                </button>
                <button
                  type="button"
                  data-testid="calendar-month-view"
                  className={`q-button q-button--mini${view === "month" ? "" : " q-button--quiet"}`}
                  aria-pressed={view === "month"}
                  onClick={() => {
                    setView("month");
                  }}
                >
                  {strings.calendar.monthView}
                </button>
                <button
                  type="button"
                  data-testid="calendar-previous"
                  className="q-button q-button--mini q-button--quiet w-8 min-w-[2rem] text-center shrink-0"
                  aria-label={strings.calendar.previous}
                  onClick={() => {
                    moveSteps(-1);
                  }}
                >
                  ←
                </button>
                <button
                  type="button"
                  data-testid="calendar-next"
                  className="q-button q-button--mini q-button--quiet w-8 min-w-[2rem] text-center shrink-0"
                  aria-label={strings.calendar.next}
                  onClick={() => {
                    moveSteps(1);
                  }}
                >
                  →
                </button>
              </div>
            </div>
          </div>
          <div
            className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2"
            data-testid="calendar-legend-row"
          >
            <AttentionLegend />
            <div className="flex items-center gap-2" data-testid="calendar-category-filter">
              <Label htmlFor="calendar-category" className="text-sm text-muted-foreground">
                {strings.calendar.categoryLabel}
              </Label>
              <NativeSelect
                id="calendar-category"
                data-testid="calendar-category"
                value={category}
                onChange={(event) => {
                  setCategory(event.target.value as Category);
                }}
              >
                <option value="all">{strings.calendar.categoryAll}</option>
                <option value="activity">{strings.calendar.categoryActivities}</option>
                <option value="rfi">{strings.calendar.categoryRfis}</option>
              </NativeSelect>
            </div>
          </div>
          {view === "month" ? (
            <MonthGrid cells={cellsFor(cursor.year, cursor.month)} onSelectDay={setOpenDay} />
          ) : view === "quarter" ? (
            <QuarterGrid months={quarterMonths} onSelectDay={setOpenDay} />
          ) : (
            <div data-testid="calendar-year-grid">
              <YearGrid
                year={cursor.year}
                activities={yearActivities.map(asScheduleActivity)}
                rfis={yearRfis}
                periods={periodList.map((one) => ({ ...one, note: null }))}
                showShutdown
                showRamadan
              />
            </div>
          )}
        </div>
      </div>

      {/* 2. What the registers hold, month by month. */}
      <Section heading={strings.calendar.visualHeading} lede={strings.calendar.visualLede}>
        <div data-testid="calendar-visual">
          <StackedBarChart
            ariaLabel={strings.calendar.visualHeading}
            series={[
              { key: "agreed", label: strings.calendar.seriesAgreed },
              { key: "waiting", label: strings.calendar.seriesWaiting },
              { key: "draft", label: strings.calendar.seriesDraft },
              { key: "rfiAgreed", label: strings.calendar.seriesRfiAgreed },
              { key: "rfiWaiting", label: strings.calendar.seriesRfiWaiting },
            ]}
            data={chartData}
            tickInterval="preserveStartEnd"
            xAxisLabel={strings.calendar.visualMonthAxis}
          />
          {candidates.kind === "ready" && (
            <p className="q-note" data-testid="calendar-unplanned-count">
              {strings.calendar.unplannedCount(candidates.data.items.length)}{" "}
              <a className="q-link" href={`#${UNPLANNED_ANCHOR}`}>
                {strings.calendar.unplannedHeading}
              </a>
            </p>
          )}
        </div>
      </Section>

      {/* 3. The registers. */}
      <Section heading={strings.calendar.listHeading}>
        <div className="flex flex-wrap items-center gap-4 mb-4" data-testid="calendar-filters">
          <div className="flex items-center gap-3" data-testid="calendar-discipline-filter">
            <Label
              htmlFor="calendar-discipline"
              className="text-xs uppercase tracking-wider text-muted-foreground whitespace-nowrap"
            >
              {strings.matrix.disciplineAxis}
            </Label>
            <NativeSelect
              id="calendar-discipline"
              className="w-auto min-w-[14rem]"
              value={filters.discipline ?? ""}
              onChange={(event) => {
                setFilters((prev) => ({
                  ...prev,
                  discipline: event.target.value === "" ? null : event.target.value,
                }));
              }}
            >
              <option value="">{strings.matrix.allDisciplines}</option>
              {disciplineOptions.map((option) => (
                <option key={option.token} value={option.token}>
                  {option.label}
                </option>
              ))}
            </NativeSelect>
          </div>
          {planningProjects.kind === "ready" && planningProjects.data.length > 0 && (
            <div className="flex items-center gap-2" data-testid="calendar-project-filter">
              <Label
                htmlFor="calendar-project"
                className="text-xs uppercase tracking-wider text-muted-foreground whitespace-nowrap"
              >
                {strings.portfolio.columnProject}
              </Label>
              <NativeSelect
                id="calendar-project"
                className={`w-auto min-w-[14rem] ${PICKER_CLASS}`}
                value={filters.projectId ?? ""}
                onChange={(event) => {
                  setFilters((prev) => ({
                    ...prev,
                    projectId: event.target.value === "" ? null : event.target.value,
                  }));
                }}
              >
                <option value="">{strings.portfolio.projectsHeading}</option>
                {planningProjects.data.map((option) => (
                  <PickerOption
                    key={option.id}
                    value={option.id}
                    identifier={option.humanId}
                    name={option.name}
                    width={pickerWidth(planningProjects.data.map((p) => p.humanId))}
                  />
                ))}
              </NativeSelect>
            </div>
          )}
        </div>
        {activities.kind === "ready" ? (
          yearActivities.length === 0 ? (
            <EmptyState title={strings.calendar.listEmpty} body={strings.calendar.lede} />
          ) : (
            <DataTable
              data={yearActivities}
              columns={activityColumns}
              caption={strings.calendar.listHeading}
              testId="calendar"
              getRowId={(row) => row.id}
            />
          )
        ) : activities.kind === "loading" ? (
          <LoadingLine label={strings.calendar.loading} />
        ) : (
          <AbsencePanel />
        )}
      </Section>

      <Section heading={strings.calendar.rfiRegisterHeading}>
        {rfis.kind === "ready" ? (
          yearRfis.length === 0 ? (
            <p className="q-note">{strings.calendar.rfiRegisterEmpty}</p>
          ) : (
            <DataTable
              data={yearRfis}
              columns={rfiColumns}
              caption={strings.calendar.rfiRegisterHeading}
              testId="calendar-rfi"
              getRowId={(row) => row.id}
            />
          )
        ) : rfis.kind === "loading" ? (
          <LoadingLine label={strings.common.loading} />
        ) : (
          <AbsencePanel />
        )}
      </Section>

      <Section
        heading={strings.calendar.unplannedHeading}
        lede={strings.calendar.unplannedLede}
        id={UNPLANNED_ANCHOR}
      >
        {candidates.kind === "failed" || candidates.kind === "absence" ? (
          <AbsencePanel />
        ) : candidates.kind === "ready" ? (
          unplannedItems.length === 0 ? (
            <p className="q-note">{strings.calendar.unplannedEmpty}</p>
          ) : (
            <DataTable
              data={unplannedItems}
              columns={unplannedColumns}
              caption={strings.calendar.unplannedHeading}
              testId="unplanned"
              getRowId={(row) => row.id}
            />
          )
        ) : (
          <LoadingLine label={strings.common.loading} />
        )}
      </Section>

      <QDrawer
        open={openDay !== null}
        title={openDay ?? ""}
        onClose={() => {
          setOpenDay(null);
        }}
      >
        {dayActivities.length === 0 && dayRfis.length === 0 ? (
          <EmptyState title={strings.calendar.listEmpty} body={strings.calendar.lede} />
        ) : (
          <ul className="q-cal__list">
            {dayActivities.map((activity) => (
              <li key={activity.id} className="q-cal__row">
                <span className="q-cal__row-top">
                  <strong>{activity.humanId}</strong>
                  <StateChip state={activity.status} label={activity.statusLabel} />
                </span>
                <span className="q-cal__row-meta">
                  {strings.schedules.negotiation[activity.negotiationState] ?? ""}
                </span>
                <span className="q-cal__row-meta">
                  {activity.contractorPartyName}
                  {activity.priority === null ? "" : ` · ${activity.priority}`}
                </span>
                <span className="q-cal__row-meta">{activity.verificationTypeLabel}</span>
                {activity.assessorDisplayNames.length > 0 && (
                  <span className="q-cal__row-meta">
                    {strings.calendar.assessors}: {activity.assessorDisplayNames.join(", ")}
                  </span>
                )}
                <Link className="q-link" to={`/activities/${activity.id}`}>
                  {strings.calendar.openRecord}
                </Link>
              </li>
            ))}
            {dayRfis.map((one) => (
              <li key={one.id} className="q-cal__row">
                <span className="q-cal__row-top">
                  <strong>{one.humanId}</strong>
                  <span className="q-note">{strings.rfis.dateState[one.dateState] ?? ""}</span>
                </span>
                <span className="q-cal__row-meta">{one.scope}</span>
                <span className="q-cal__row-meta">{one.contractorPartyName}</span>
                <Link className="q-link" to={`/rfis/${one.id}`}>
                  {strings.calendar.openRecord}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </QDrawer>

      {planOpen && planningProjects.kind === "ready" && (
        <CalendarPlanDialog
          projects={planningProjects.data}
          onClose={closePlan}
          onPlanned={(message, scheduleId) => {
            closePlan();
            setToast({ message, scheduleId });
            setReload((n) => n + 1);
          }}
        />
      )}
    </main>
  );
}

/**
 * Plan an activity from the calendar (wave W2, his words): "choose the contract, the
 * activity, the discipline, the dates, the contractor; it goes for approval and becomes
 * a planned activity". It lands in the project's open draft schedule for the date's
 * year (one is opened when the year has none), and is sent for approval in the same
 * act when the planner may submit it.
 */
export function CalendarPlanDialog({
  projects,
  onClose,
  onPlanned,
  initialCandidate,
  initialProjectId,
  initialVerificationTypeCode,
}: {
  readonly projects: readonly PlanningProject[];
  readonly onClose: () => void;
  readonly onPlanned: (message: string, scheduleId: string) => void;
  readonly initialCandidate?: Candidate | null;
  readonly initialProjectId?: string;
  readonly initialVerificationTypeCode?: string | undefined;
}): React.JSX.Element {
  const token = useToken();
  const [projectId, setProjectId] = useState(
    initialProjectId && projects.some((p) => p.id === initialProjectId)
      ? initialProjectId
      : (projects[0]?.id ?? ""),
  );
  const [contractId, setContractId] = useState(initialCandidate?.contractId ?? "");
  const [itpId, setItpId] = useState(initialCandidate?.itpId ?? "");
  const [candidateId, setCandidateId] = useState(initialCandidate?.id ?? "");
  const [userTypeId, setUserTypeId] = useState<string | null>(null);
  const [plannedDate, setPlannedDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [purpose, setPurpose] = useState("");

  const project = projects.find((one) => one.id === projectId);
  const [submit, setSubmit] = useState(project?.maySubmit ?? false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const contracts = useQuery(
    (signal) =>
      projectId.length > 0
        ? listContracts(token, { projectId, signal })
        : Promise.resolve({ total: 0, items: [] }),
    [token, projectId],
  );
  // Wave Experience A: the plans and items are read FOR THE CHOSEN CONTRACT (the server's
  // own narrowing), so the list is never cut short by somebody else's long to-do list.
  const candidates = useQuery(
    (signal) =>
      contractId.length > 0
        ? listCandidates(token, { unplannedOnly: true, contractId, signal })
        : Promise.resolve({ total: 0, items: [] as Candidate[] }),
    [token, contractId],
  );
  const taxonomies = useQuery(() => getTaxonomies(token), [token]);

  const defaultTypeId =
    initialVerificationTypeCode && taxonomies.kind === "ready"
      ? (taxonomies.data.verificationTypes.find((one) => one.token === initialVerificationTypeCode)
          ?.id ?? "")
      : "";
  const typeId = userTypeId ?? defaultTypeId;

  const projectContracts = contracts.kind === "ready" ? contracts.data.items : [];
  const contract = projectContracts.find((one) => one.id === contractId);
  const contractCandidates = (candidates.kind === "ready" ? candidates.data.items : []).filter(
    (one) => one.mayPlan && one.contractId === contractId,
  );
  // The contract's plans, each once: identifier and discipline (the item's category).
  const plans = [
    ...new Map(
      contractCandidates.map((one) => [
        one.itpId,
        { humanId: one.itpHumanId, discipline: one.disciplineLabel },
      ]),
    ).entries(),
  ];
  const planWidth = pickerWidth(plans.map(([, one]) => one.humanId));
  const offered = contractCandidates.filter((one) => itpId.length === 0 || one.itpId === itpId);
  const candidate = offered.find((one) => one.id === candidateId);
  const types = (taxonomies.kind === "ready" ? taxonomies.data.verificationTypes : []).filter(
    (one) => one.id !== undefined,
  );
  const contractorId = candidate?.contractorPartyId ?? contract?.contractorPartyId ?? "";
  const contractorName = candidate?.contractorPartyName ?? contract?.contractorPartyName ?? "";
  const missing =
    contractId.length === 0
      ? strings.calendar.planNeedsContract
      : typeId.length === 0
        ? strings.schedules.planNeedsType
        : plannedDate.length === 0
          ? strings.schedules.planNeedsDate
          : null;

  async function plan(): Promise<void> {
    if (missing !== null) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const planned = await planFromCalendar(token, {
        project_id: projectId,
        contract_id: contractId,
        contractor_party_id: contractorId,
        planning_candidate_id: candidate?.id ?? null,
        verification_type_id: typeId,
        planned_date: plannedDate,
        planned_end_date: endDate.length > 0 ? endDate : null,
        purpose: purpose.trim().length > 0 ? purpose.trim() : null,
        submit: submit && (project?.maySubmit ?? false),
      });
      onPlanned(
        strings.calendar.planDone(
          planned.activityHumanId,
          planned.scheduleHumanId,
          planned.submitted,
        ),
        planned.scheduleId,
      );
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <ActionDialog open title={strings.calendar.planHeading} onClose={onClose} error={error} wide>
      <p className="q-shell__lede">{strings.calendar.planLede}</p>
      <div className="q-field">
        <label className="q-field__label" htmlFor="calendar-plan-project">
          {strings.calendar.planProjectLabel}
        </label>
        <ServerField field="project_id">
          <NativeSelect
            id="calendar-plan-project"
            data-testid="calendar-plan-project"
            className={PICKER_CLASS}
            value={projectId}
            onChange={(event) => {
              setProjectId(event.target.value);
              setContractId("");
              setCandidateId("");
              setSubmit(projects.find((one) => one.id === event.target.value)?.maySubmit ?? false);
            }}
          >
            {projects.map((one) => (
              <PickerOption
                key={one.id}
                value={one.id}
                identifier={one.humanId}
                name={one.name}
                width={pickerWidth(projects.map((p) => p.humanId))}
              />
            ))}
          </NativeSelect>
        </ServerField>
      </div>
      <div className="q-field">
        <label className="q-field__label" htmlFor="calendar-plan-contract">
          {strings.calendar.planContractLabel}
        </label>
        <ServerField field="contract_id">
          <NativeSelect
            id="calendar-plan-contract"
            data-testid="calendar-plan-contract"
            className={PICKER_CLASS}
            value={contractId}
            onChange={(event) => {
              setContractId(event.target.value);
              setCandidateId("");
            }}
          >
            <option value="" />
            {projectContracts.map((one) => (
              <PickerOption
                key={one.id}
                value={one.id}
                identifier={one.humanId}
                name={one.contractorPartyName}
                width={pickerWidth(projectContracts.map((c) => c.humanId))}
              />
            ))}
          </NativeSelect>
        </ServerField>
      </div>
      {contractorName.length > 0 && (
        <p className="q-note" data-testid="calendar-plan-contractor">
          {strings.schedules.contractorLabel}: {contractorName}
        </p>
      )}
      <div className="q-field">
        <label className="q-field__label" htmlFor="calendar-plan-type">
          {strings.schedules.typeSelectLabel}
        </label>
        <ServerField field="verification_type_id">
          <NativeSelect
            id="calendar-plan-type"
            data-testid="calendar-plan-type"
            value={typeId}
            onChange={(event) => {
              setUserTypeId(event.target.value);
            }}
          >
            <option value="" />
            {types.map((one) => (
              <option key={one.token} value={one.id}>
                {one.label}
              </option>
            ))}
          </NativeSelect>
        </ServerField>
      </div>
      {contractCandidates.length > 0 && (
        <div className="q-field">
          <label className="q-field__label" htmlFor="calendar-plan-itp">
            {strings.calendar.planItpLabel}
          </label>
          <NativeSelect
            id="calendar-plan-itp"
            data-testid="calendar-plan-itp"
            className={PICKER_CLASS}
            value={itpId}
            onChange={(event) => {
              setItpId(event.target.value);
              setCandidateId("");
            }}
          >
            <option value="">{strings.calendar.planItpAll}</option>
            {plans.map(([id, one]) => (
              <PickerOption
                key={id}
                value={id}
                identifier={one.humanId}
                name={one.discipline}
                width={planWidth}
              />
            ))}
          </NativeSelect>
        </div>
      )}
      <div className="q-field">
        <label className="q-field__label" htmlFor="calendar-plan-candidate">
          {strings.calendar.planCandidateLabel}
        </label>
        <ServerField field="planning_candidate_id">
          <NativeSelect
            id="calendar-plan-candidate"
            data-testid="calendar-plan-candidate"
            value={candidateId}
            onChange={(event) => {
              setCandidateId(event.target.value);
            }}
          >
            <option value="">{strings.calendar.planCandidateNone}</option>
            {offered.map((one) => (
              <option key={one.id} value={one.id}>
                {one.itpHumanId} {one.itemNo} {one.activityDescription} ({one.interventionLabel})
              </option>
            ))}
          </NativeSelect>
        </ServerField>
      </div>
      <div className="q-field">
        <label className="q-field__label" htmlFor="calendar-plan-date">
          {strings.schedules.plannedDateLabel}
        </label>
        <ServerField field="planned_date">
          <Input
            id="calendar-plan-date"
            type="date"
            value={plannedDate}
            onChange={(event) => {
              setPlannedDate(event.target.value);
            }}
          />
        </ServerField>
      </div>
      <div className="q-field">
        <label className="q-field__label" htmlFor="calendar-plan-end-date">
          {strings.schedules.endDateLabel}
        </label>
        <ServerField field="planned_end_date">
          <Input
            id="calendar-plan-end-date"
            type="date"
            value={endDate}
            onChange={(event) => {
              setEndDate(event.target.value);
            }}
          />
        </ServerField>
      </div>
      <DateCheck
        projectId={projectId.length > 0 ? projectId : null}
        plannedDate={plannedDate}
        plannedEndDate={endDate.length > 0 ? endDate : null}
      />
      <div className="q-field">
        <label className="q-field__label" htmlFor="calendar-plan-purpose">
          {strings.schedules.purposeLabel}
        </label>
        <ServerField field="purpose">
          <Input
            id="calendar-plan-purpose"
            value={purpose}
            onChange={(event) => {
              setPurpose(event.target.value);
            }}
          />
        </ServerField>
      </div>
      {project?.maySubmit === true ? (
        <div className="flex items-center gap-2">
          <Checkbox
            id="calendar-plan-submit"
            data-testid="calendar-plan-submit"
            checked={submit}
            onCheckedChange={(checked) => {
              setSubmit(checked === true);
            }}
          />
          <Label htmlFor="calendar-plan-submit" className="text-sm font-normal cursor-pointer">
            {strings.calendar.planSubmitLabel}
          </Label>
        </div>
      ) : (
        <p className="q-note">{strings.calendar.planSubmitNotYours}</p>
      )}
      {missing !== null && <p className="q-note">{missing}</p>}
      <div className="q-actions-row">
        <button
          type="button"
          className="q-button q-mini--primary"
          data-testid="calendar-plan-confirm"
          disabled={busy || missing !== null}
          onClick={() => {
            void plan();
          }}
        >
          {strings.calendar.planActivity}
        </button>
        <button type="button" className="q-button q-button--quiet" onClick={onClose}>
          {strings.common.cancel}
        </button>
      </div>
    </ActionDialog>
  );
}
